import React, { createContext, useContext, useEffect, useState } from 'react'
import { api, UNAUTHORIZED_EVENT, ApiError } from '@/lib/api'
import type { User } from '@/types'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<User | null>
  register: (name: string, email: string, password: string) => Promise<User | null>
  signup: (data: SignupData) => Promise<SignupResult>
  forgotPassword: (email: string) => Promise<void>
  logout: () => void
  updateUser: (updates: Partial<User>) => void
  switchOrganization: (organizationId: string) => Promise<User | undefined>
  authenticateWithToken: (token: string) => Promise<User | null>
  refreshUser: () => Promise<User | null>
}

interface SignupData {
  firstName: string
  lastName: string
  email: string
  password: string
  organizationName?: string
  invitationToken?: string
  jobTitle?: string
  department?: string
}

interface SignupResult {
  requiresVerification: boolean
  verificationToken?: string
  email?: string
  user?: User | null
  message?: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const mapBackendOrganizationPlan = (plan?: string): 'free' | 'professional' | 'enterprise' => {
    const normalized = (plan ?? 'free').toString().toLowerCase()
    if (normalized.includes('pro')) return 'professional'
    if (normalized.includes('enterprise')) return 'enterprise'
    return 'free'
  }

  const mapBackendRole = (role?: string): 'owner' | 'admin' | 'manager' | 'staff' => {
    const normalized = (role ?? 'staff').toString().toLowerCase()
    if (normalized === 'owner') return 'owner'
    if (normalized === 'admin') return 'admin'
    if (normalized === 'manager') return 'manager'
    return 'staff'
  }

  const mapBackendUser = (raw: any): User => {
    const organizations = Array.isArray(raw?.organizations)
      ? raw.organizations.map((org: any) => ({
          id: org.id,
          name: org.name,
          avatar: org.avatar ?? undefined,
          plan: mapBackendOrganizationPlan(org.plan),
          role: mapBackendRole(org.role),
          industry: org.industry ?? undefined,
          companySize: org.companySize ?? undefined,
          description: org.description ?? undefined,
          timeZone: org.timeZone ?? org.time_zone ?? undefined,
          createdAt: org.createdAt ?? org.created_at ?? new Date().toISOString(),
          updatedAt: org.updatedAt ?? org.updated_at ?? new Date().toISOString(),
        }))
      : []

    const fullName = [raw?.firstName ?? raw?.first_name, raw?.lastName ?? raw?.last_name]
      .filter(Boolean)
      .join(' ')
      .trim()

    return {
      id: raw.id,
      email: raw.email,
      name: fullName || raw.email,
      firstName: raw.firstName ?? raw.first_name ?? '',
      lastName: raw.lastName ?? raw.last_name ?? '',
      organizationRole: mapBackendRole(raw.role ?? organizations[0]?.role),
      teamRole: raw.teamRole ?? undefined,
      avatar: raw.avatar ?? undefined,
      bio: raw.bio ?? undefined,
      department: raw.department ?? undefined,
      position: raw.jobTitle ?? raw.job_title ?? undefined,
      phone: raw.phone ?? undefined,
      status: raw.isActive === false ? 'inactive' : 'active',
      currentOrganizationId: raw.currentOrganizationId ?? raw.current_organization_id ?? organizations[0]?.id,
      organizations,
      createdAt: raw.createdAt ?? raw.created_at ?? new Date().toISOString(),
      updatedAt: raw.updatedAt ?? raw.updated_at ?? new Date().toISOString(),
      lastLoginAt: raw.lastLoginAt ?? raw.last_login_at ?? undefined,
      onboardingCompleted: raw.onboardingCompleted ?? raw.onboarding_completed ?? false,
      isActive: raw.isActive ?? raw.is_active ?? true,
      role: raw.role ? String(raw.role).toLowerCase() as User['role'] : undefined,
    }
  }

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get<any>('/auth/me')
      const payload = response?.data ?? response
      if (payload) {
        const mapped = mapBackendUser(payload)
        setUser(mapped)
        return mapped
      }
      return null
    } catch (error) {
      localStorage.removeItem('auth_token')
      setUser(null)
      throw error
    }
  }

  // Initialize auth state on mount
  useEffect(() => {
    const initialize = async () => {
      try {
        await fetchCurrentUser()
      } catch (error) {
        localStorage.removeItem('auth_token')
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    initialize()
  }, [])

  useEffect(() => {
    console.info('👤 AUTH user state changed', user)
  }, [user])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleUnauthorized = () => {
      setUser(null)
      setLoading(false)
    }

    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized)

    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized)
    }
  }, [])

  const authenticateWithToken = async (token: string): Promise<User | null> => {
    localStorage.setItem('auth_token', token)
    try {
      const currentUser = await fetchCurrentUser()
      return currentUser ?? null
    } catch (error) {
      localStorage.removeItem('auth_token')
      setUser(null)
      throw error
    }
  }

  const login = async (email: string, password: string): Promise<User | null> => {
    console.log('🔑 AUTH: Login function called with:', { email, password: password.length + ' chars' })

    try {
      console.log('🔑 AUTH: Making API call to /auth/login...')
      const response = await api.post<any>('/auth/login', {
        email,
        password,
      })

      const payload = response?.data ?? response

      console.log('🔑 AUTH: API response received:', {
        hasPayload: !!payload,
        hasUser: !!payload?.user,
        hasToken: !!payload?.token,
      })

      if (!payload?.token) {
        throw new Error('Login response missing token')
      }

      const currentUser = await authenticateWithToken(payload.token)
      console.log('🔑 AUTH: User state updated successfully', { userId: currentUser?.id })
      return currentUser ?? null
    } catch (error) {
      console.error('🔑 AUTH: Login failed with error:', error)

      if (error instanceof ApiError) {
        let parsed: any = null

        try {
          parsed = JSON.parse(error.message)
        } catch (parseError) {
          console.warn('🔑 AUTH: Unable to parse auth API error payload', parseError)
        }

        const mappedError = new Error(parsed?.message ?? parsed?.error ?? 'Login failed') as Error & {
          code?: string
          status?: number
          data?: unknown
        }

        mappedError.code = parsed?.code ?? undefined
        mappedError.status = error.status
        mappedError.data = parsed?.data ?? undefined

        throw mappedError
      }

      throw error
    }
  }

  const register = async (name: string, email: string, password: string): Promise<User | null> => {
    const response = await api.post<any>('/auth/register', {
      name,
      email,
      password,
    })

    const payload = response?.data ?? response

    if (!payload?.token) {
      throw new Error('Registration response missing token')
    }

    return authenticateWithToken(payload.token)
  }

  const signup = async (data: SignupData): Promise<SignupResult> => {
    const response = await api.post<any>('/auth/signup', data)
    const payload = response?.data ?? response

    if (payload?.token && payload?.user) {
      const currentUser = await authenticateWithToken(payload.token)
      return {
        requiresVerification: false,
        user: currentUser,
        message: response?.message,
      }
    }

    if (payload?.requiresVerification && payload?.verificationToken) {
      localStorage.removeItem('auth_token')
      setUser(null)
      return {
        requiresVerification: true,
        verificationToken: payload.verificationToken,
        email: payload.email ?? data.email,
        message: response?.message,
        user: null,
      }
    }

    return {
      requiresVerification: false,
      user: null,
      message: response?.message,
    }
  }

  const forgotPassword = async (email: string) => {
    await api.post('/auth/forgot-password', { email })
  }

  const logout = () => {
    localStorage.removeItem('auth_token')
    setUser(null)
    // Optionally call logout endpoint
    api.post('/auth/logout').catch(() => {
      // Ignore errors on logout
    })
  }

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      const next = { ...user, ...updates }
      console.info('🔄 AUTH updateUser', { updates, next })
      setUser(next)
    } else {
      console.warn('⚠️ AUTH updateUser skipped because user is null', updates)
    }
  }

  const switchOrganization = async (organizationId: string) => {
    let baselineUser = user ?? await fetchCurrentUser()

    if (!baselineUser) {
      throw new Error('No user logged in')
    }

    const targetOrg = baselineUser.organizations.find(org => org.id === organizationId)

    if (!targetOrg) {
      console.info('🔁 AUTH switchOrganization: organization not found locally; continuing to rely on backend validation', {
        organizationId,
        knownOrganizations: baselineUser.organizations.map((org) => ({ id: org.id, name: org.name })),
      })
    }

    try {
      const response = await api.post<any>('/auth/switch-organization', { organizationId })
      console.info('🔁 AUTH switchOrganization: response', response)
      const payload = response?.data ?? response

      if (payload?.token) {
        localStorage.setItem('auth_token', payload.token)
      }

      if (payload?.user) {
        const mappedUser = mapBackendUser(payload.user)
        console.info('🔁 AUTH switchOrganization: backend returned user', mappedUser)
        setUser(mappedUser)
        return mappedUser
      }

      const updatedUser = await fetchCurrentUser()
      console.info('🔁 AUTH switchOrganization: fetchCurrentUser fallback', updatedUser)
      if (updatedUser) {
        return updatedUser
      }

      if (baselineUser) {
        setUser({
          ...baselineUser,
          currentOrganizationId: organizationId,
          organizationRole: targetOrg?.role ?? baselineUser.organizationRole,
        })
        return {
          ...baselineUser,
          currentOrganizationId: organizationId,
          organizationRole: targetOrg?.role ?? baselineUser.organizationRole,
        }
      }
    } catch (error) {
      console.error('Failed to switch organization:', error)
      throw error
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    signup,
    forgotPassword,
    logout,
    updateUser,
    switchOrganization,
    authenticateWithToken,
    refreshUser: fetchCurrentUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
